/**
 * Connection-param handshake between the embedding host and the app frame.
 *
 * An embedded app needs three things to open its back-connection: the gateway
 * URL, its own `appId`, and a session identity token. The token is a **secret**,
 * so it must not ride in the iframe `src` — the preview surface that serves the
 * app is unauthenticated, and a URL lands in access logs, `Referer` headers, and
 * browser history. Instead the host posts it over `postMessage`, and the app
 * asks for it with a ready ping once its script is running.
 *
 * The host cannot simply post on iframe `load`: it has no way to know when the
 * app's script is listening. So the app speaks first.
 *
 * ```text
 * app  → host   { type: 'skaile_app_ready',  appId }        targetOrigin = parentOrigin
 * host → app    { type: 'skaile_app_params', appId, url, token }   targetOrigin = appOrigin
 * ```
 *
 * Both sides pin the peer: the app checks `event.origin === parentOrigin` and
 * that the message came from its own parent; the host checks the origin and that
 * `event.source` is the very iframe it rendered. The app learns the expected
 * parent origin from a **non-secret** query param on its `src`
 * ({@link PARENT_ORIGIN_QUERY_PARAM}), which is safe to leak precisely because it
 * is not a credential.
 *
 * @category App SDK
 * @since 3.5.0
 */

/**
 * Query param on the app's iframe `src` carrying the embedder's origin. Not a
 * secret — it is the value the app pins its `postMessage` peer to.
 */
export const PARENT_ORIGIN_QUERY_PARAM = "skaileParentOrigin";

/** Default timeout waiting for the host's params reply. */
export const DEFAULT_PARAMS_TIMEOUT_MS = 10_000;

/** First gap between ready pings. Grows by {@link PING_BACKOFF_FACTOR} up to {@link MAX_PING_INTERVAL_MS}. */
export const INITIAL_PING_INTERVAL_MS = 100;

/**
 * Ceiling for the ready-ping gap. The host mints a fresh single-use ticket for
 * *every* ping it answers, so pinging is not free — back off rather than hammer.
 */
export const MAX_PING_INTERVAL_MS = 1_000;

const PING_BACKOFF_FACTOR = 2;

/** App → host. Announces the app frame is listening and wants its params. */
export type SkaileAppReadyMessage = {
  type: "skaile_app_ready";
  /** The app asking. The host answers only for an app it actually embedded. */
  appId: string;
};

/** Host → app. Carries the gateway URL and the single-use session identity token. */
export type SkaileAppParamsMessage = {
  type: "skaile_app_params";
  /** Echoed back so a frame embedding several apps can disambiguate. */
  appId: string;
  /** The gateway app-channel URL, e.g. `wss://platform/app-connect`. */
  url: string;
  /** Single-use session identity token. Consumed by the gateway at `app_hello`. */
  token: string;
};

/** Resolved params, ready to hand to `createWebSocketAppTransport` + `createSkaileApp`. */
export type AppConnectionParams = {
  url: string;
  appId: string;
  token: string;
};

/** The subset of `postMessage` targets this module needs. */
export type PostMessageTarget = {
  postMessage(message: unknown, targetOrigin: string): void;
};

/** Structural view of a `MessageEvent`, so tests need no DOM. */
export type MessageEventLike = {
  readonly origin: string;
  readonly source: unknown;
  readonly data: unknown;
};

/** Structural view of the `window` bits this module needs. Injectable for tests. */
export type AppWindowLike = {
  readonly parent: PostMessageTarget | null;
  readonly location: { readonly search: string };
  addEventListener(type: "message", handler: (event: MessageEventLike) => void): void;
  removeEventListener(type: "message", handler: (event: MessageEventLike) => void): void;
};

/**
 * Options for {@link resolveAppConnectionParams}.
 *
 * @category App SDK
 * @since 3.5.0
 */
export interface ResolveAppConnectionParamsOptions {
  /** The embedded app's stable id. Must match the params the host replies with. */
  appId: string;
  /**
   * Expected origin of the embedding host. Defaults to the
   * {@link PARENT_ORIGIN_QUERY_PARAM} query param on the current URL. Never `"*"`
   * or `"null"` — an unpinned peer defeats the point of not putting the token in
   * the URL.
   */
  parentOrigin?: string;
  /** How long to wait for the host's reply. Defaults to {@link DEFAULT_PARAMS_TIMEOUT_MS}. */
  timeoutMs?: number;
  /** Window to listen and post on. Defaults to the global. Injectable for tests. */
  windowRef?: AppWindowLike;
}

function readParentOriginFromQuery(search: string): string | null {
  const value = new URLSearchParams(search).get(PARENT_ORIGIN_QUERY_PARAM);
  return value && value.length > 0 ? value : null;
}

function isParamsMessage(data: unknown, appId: string): data is SkaileAppParamsMessage {
  if (!data || typeof data !== "object") return false;
  const m = data as Partial<SkaileAppParamsMessage>;
  // Trim before the emptiness check: a whitespace-only token would survive to
  // the gateway and be refused there. Failing here is the clearer error.
  return (
    m.type === "skaile_app_params" &&
    m.appId === appId &&
    typeof m.url === "string" &&
    m.url.trim().length > 0 &&
    typeof m.token === "string" &&
    m.token.trim().length > 0
  );
}

/**
 * Ask the embedding host for this app's connection params.
 *
 * Posts a {@link SkaileAppReadyMessage} to the parent and resolves with the
 * matching {@link SkaileAppParamsMessage}. Messages from any other origin, from
 * any window other than the parent, or for another `appId` are ignored — not
 * rejected — because a page may legitimately receive unrelated `postMessage`
 * traffic.
 *
 * Call it again to re-request a fresh token: the token is single-use, so a
 * reconnecting transport needs a new one (see `createSkaileApp`'s `reconnect`).
 *
 * @throws when not embedded, when the parent origin cannot be determined or is
 *   unpinnable, or when the host does not reply within `timeoutMs`.
 *
 * @category App SDK
 * @since 3.5.0
 */
export function resolveAppConnectionParams(
  options: ResolveAppConnectionParamsOptions,
): Promise<AppConnectionParams> {
  const { appId, timeoutMs = DEFAULT_PARAMS_TIMEOUT_MS } = options;
  const win =
    options.windowRef ?? (globalThis as unknown as { window?: AppWindowLike }).window ?? null;
  if (!win) {
    return Promise.reject(
      new Error("resolveAppConnectionParams requires a window (pass `windowRef` off-browser)"),
    );
  }

  const parent = win.parent;
  // A top-level page's `parent` is itself. An app that is not embedded has no
  // host to ask, and posting to itself would hang until the timeout.
  if (!parent || (parent as unknown) === (win as unknown)) {
    return Promise.reject(
      new Error("app is not embedded — no parent frame to request params from"),
    );
  }

  const parentOrigin = options.parentOrigin ?? readParentOriginFromQuery(win.location.search);
  if (!parentOrigin) {
    return Promise.reject(
      new Error(
        `cannot determine the host origin: pass \`parentOrigin\` or set the \`${PARENT_ORIGIN_QUERY_PARAM}\` query param`,
      ),
    );
  }
  // `*` would broadcast the ready ping to whatever is above us, and `null` (an
  // opaque, sandboxed origin) cannot be pinned at all. Both make the peer check
  // meaningless, and the token would then be posted to an unverified window.
  if (parentOrigin === "*" || parentOrigin === "null") {
    return Promise.reject(new Error(`refusing to use an unpinnable host origin: ${parentOrigin}`));
  }

  // Bind the narrowed peer once: the guards above proved both are non-null, but
  // that narrowing does not survive into the hoisted `ping` declaration below.
  const peer: PostMessageTarget = parent;
  const peerOrigin: string = parentOrigin;

  return new Promise<AppConnectionParams>((resolve, reject) => {
    let settled = false;
    let pingTimer: ReturnType<typeof setTimeout> | undefined;
    let pingInterval = INITIAL_PING_INTERVAL_MS;

    const cleanup = () => {
      clearTimeout(timer);
      clearTimeout(pingTimer);
      win.removeEventListener("message", onMessage);
    };

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(
        new Error(`timed out after ${timeoutMs}ms waiting for connection params from the host`),
      );
    }, timeoutMs);

    function onMessage(event: MessageEventLike): void {
      if (settled) return;
      // Pin the peer on both axes. Origin alone is not enough: any frame served
      // from the host's origin could otherwise answer.
      if (event.origin !== peerOrigin || event.source !== peer) return;
      if (!isParamsMessage(event.data, appId)) return;
      settled = true;
      cleanup();
      resolve({ url: event.data.url, appId, token: event.data.token });
    }

    win.addEventListener("message", onMessage);

    const ready: SkaileAppReadyMessage = { type: "skaile_app_ready", appId };

    /**
     * Keep asking until the host answers. The host only mints in response to a
     * ping and never speaks unprompted, so a single ping is a race the app loses
     * whenever it boots before the host's own state has settled — the ping is
     * dropped and nothing re-asks. Backs off because each answered ping costs the
     * host a single-use ticket.
     */
    function ping(): void {
      if (settled) return;
      try {
        peer.postMessage(ready, peerOrigin);
      } catch (err) {
        settled = true;
        cleanup();
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      pingTimer = setTimeout(ping, pingInterval);
      pingInterval = Math.min(pingInterval * PING_BACKOFF_FACTOR, MAX_PING_INTERVAL_MS);
    }

    ping();
  });
}
